import { faBuilding, faCheckCircle, faSave, faTimesCircle, faUserCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, Grid, Paper, Skeleton, Tab, Tabs, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { InputText } from '../../../../components/Inputs/InputText';
import { InputSelectMultiple } from '../../../../components/Inputs/InputSelectMultiple';
import { ModalStatusServer } from '../../../../components/ModalStatus/ModalStatusServer';
import { useLang } from '../../../../language';
import { useForm } from '../../../../hooks/useForm';
import { EmpresasServices } from '../../controllers/services/empresas.services';
import { MaestrosServices } from '../../controllers/services/maestros.services';
import { UsuariosAutorizados } from '../UsuariosAutorizados';

// Pestaña "Mis datos" del detalle de empresa. Contiene dos subpestañas:
//   1. Usuarios autorizados -> módulo UsuariosAutorizados (listado + alta directa).
//   2. Datos de la empresa   -> formulario con los campos editables.
//
// `empresa` llega ya cargado por `EmpresaDetalle`, así que no se duplica la
// petición del detalle; los valores iniciales salen de esa prop. Las listas de
// categorías y departamentos sí se piden aparte, a los endpoints públicos de
// maestros, porque son catálogos que no dependen de la empresa.
export const MisDatos = ({ setBreadcrumbLineItems, route, empresa }: any) => {

    const Lang = useLang()

    const { empresaId } = useParams()

    const [tabActivo, setTabActivo] = useState(0)

    const [fetchUpdate, fetchUpdateData, resetUpdate] = EmpresasServices.UPDATE_DATOS_EMPRESA()

    const [fetchCategoriasGET, fetchCategoriasGETData] = MaestrosServices.GET_CATEGORIAS_PUBLICAS()

    const [fetchDepartamentosGET, fetchDepartamentosGETData] = MaestrosServices.GET_DEPARTAMENTOS_PUBLICAS()

    // Los catálogos solo hacen falta en la subpestaña "Datos de la empresa", así
    // que no se piden hasta que se abre. `maestrosCargados` evita repetir la
    // petición cada vez que se vuelve a la pestaña.
    const [maestrosCargados, setMaestrosCargados] = useState(false)

    useEffect(() => {

        if (tabActivo !== 1 || maestrosCargados) return

        // GET /integra-web/categorias-publicas
        fetchCategoriasGET()

        // GET /integra-web/departamentos-publicas
        fetchDepartamentosGET()

        setMaestrosCargados(true)

    }, [tabActivo, maestrosCargados])

    // El nombre y el CIF solo se muestran: el nombre no se edita (lo mantiene el
    // administrador y el backend lo rechaza) y el CIF identifica a la empresa.
    // `dom_validada`, `si_dom` y `siarc` se editan únicamente desde el panel de
    // administración, así que aquí no aparecen.
    const [form, handleInputForm, handleFormForm] = useForm({
        nombre: '',
        cif: '',
        telefono: '',
        email: '',
        persona_nombre: '',
        persona_apellidos: '',
        categorias: [],
        departamentos: [],
    })

    useEffect(() => {

        setBreadcrumbLineItems((item: any) => [...item.slice(0, 2), route])

    }, [])

    // Carga inicial: si el detalle aún no ha llegado, no se pisa el formulario;
    // cuando llegue, se rellenan los campos.
    useEffect(() => {

        if (!empresa) return

        // `handleFormForm` sustituye el estado entero, así que se pasan todos los
        // campos del formulario. Los que aquí no se tocan (los de solo lectura)
        // se mantienen con su valor actual.
        handleFormForm({
            ...form,
            nombre: empresa.nombre || '',
            cif: empresa.cif || '',
            telefono: empresa.telefono || '',
            email: empresa.email || '',
            persona_nombre: empresa.persona_nombre || '',
            persona_apellidos: empresa.persona_apellidos || '',
            categorias: (empresa.categorias || []).map((item: any) => item.id),
            departamentos: (empresa.departamentos || []).map((item: any) => item.id),
        })

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [empresa])

    // Tras guardar se refresca el formulario con lo que devuelve el backend.
    useEffect(() => {

        const guardada = fetchUpdateData?.data?.empresa

        if (!guardada) return

        handleFormForm({
            ...form,
            nombre: guardada.nombre || form.nombre,
            cif: guardada.cif || form.cif,
            telefono: guardada.telefono || '',
            email: guardada.email || '',
            persona_nombre: guardada.persona_nombre || '',
            persona_apellidos: guardada.persona_apellidos || '',
            categorias: guardada.categorias ?? form.categorias,
            departamentos: guardada.departamentos ?? form.departamentos,
        })

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchUpdateData?.data])

    // Los selectores múltiples no pasan por `handleInputForm` del hook: MUI entrega
    // el type como string en React 17, así que se normaliza a número para que los ids
    // casen con los del catálogo.
    const handleChangeMulti = (e: any) => {

        const { name } = e.target

        const value = e.target.value
        const ids = (Array.isArray(value) ? value : [value]).map((id: any) => parseInt(id, 10))

        handleFormForm({ ...form, [name]: ids.filter((id: any) => !isNaN(id)) })

    }

    const handleGuardar = () => {

        if (!empresaId) return

        fetchUpdate({
            id: Number(empresaId),
            body: {
                telefono: form.telefono,
                email: form.email,
                persona_nombre: form.persona_nombre,
                persona_apellidos: form.persona_apellidos,
                categorias: form.categorias,
                departamentos: form.departamentos,
            }
        })

    }

    const guardando = !!fetchUpdateData?.loading

    const cargandoMaestros = !!fetchCategoriasGETData?.loading || !!fetchDepartamentosGETData?.loading

    // El formulario solo se muestra cuando ya hay detalle de empresa (viene por
    // prop desde `EmpresaDetalle`) y catálogos cargados. Mientras tanto se pintan
    // esqueletos con la misma retícula para que no salte el layout al rellenar.
    const cargandoFormulario = !empresa || cargandoMaestros

    return (
        <Paper className='p-3 p-md-4' sx={{ borderRadius: 2.5 }}>

            <Tabs
                value={tabActivo}
                onChange={(e: any, value: any) => setTabActivo(value)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ mb: 3 }}
            >
                <Tab
                    icon={<FontAwesomeIcon icon={faUserCheck} />}
                    iconPosition="start"
                    label={Lang('USUARIOS_AUTORIZADOS')}
                />

                <Tab
                    icon={<FontAwesomeIcon icon={faBuilding} />}
                    iconPosition="start"
                    label={Lang('DATOS_EMPRESA')}
                />
            </Tabs>

            {
                tabActivo === 0
                    ?
                    <UsuariosAutorizados />
                    :
                    <Box>

                        <Typography variant='h6' className="my-0 pb-1 fw-500 color-primary">
                            {Lang('DATOS_EMPRESA')}
                        </Typography>

                        {
                            cargandoFormulario
                                ?
                                // Esqueletos con la misma retícula del formulario:
                                // 6 campos de texto + 2 selectores.
                                <Grid container spacing={2} className='mt-1'>
                                    {
                                        Array.from({ length: 8 }).map((_, index) => (
                                            <Grid item xs={12} md={6} key={`skeleton-mis-datos-${index}`}>
                                                <Skeleton
                                                    variant={index >= 6 ? 'rounded' : 'text'}
                                                    height={index >= 6 ? 40 : undefined}
                                                />
                                            </Grid>
                                        ))
                                    }
                                </Grid>
                                :
                                <>
                                    <Grid container spacing={2} className='mt-1'>

                                    <Grid item xs={12} md={6}>
                                        <InputText label={Lang('NOMBRE')} name="nombre" value={form.nombre} readOnly />
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <InputText label={Lang('CIF')} name="cif" value={form.cif} readOnly />
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <InputText label={Lang('TELEFONO')} name="telefono" value={form.telefono} onChange={handleInputForm} />
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <InputText label={Lang('EMAIL')} name="email" value={form.email} onChange={handleInputForm} />
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <InputText label={Lang('NOMBRE_CONTACTO')} name="persona_nombre" value={form.persona_nombre} onChange={handleInputForm} />
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <InputText label={Lang('APELLIDOS_CONTACTO')} name="persona_apellidos" value={form.persona_apellidos} onChange={handleInputForm} />
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <InputSelectMultiple
                                            label={Lang('CATEGORIAS')}
                                            name="categorias"
                                            value={form.categorias}
                                            values={fetchCategoriasGETData?.data?.items || []}
                                            onChange={handleChangeMulti}
                                            disabled={cargandoMaestros}
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <InputSelectMultiple
                                            label={Lang('DEPARTAMENTOS')}
                                            name="departamentos"
                                            value={form.departamentos}
                                            values={fetchDepartamentosGETData?.data?.items || []}
                                            onChange={handleChangeMulti}
                                            disabled={cargandoMaestros}
                                        />
                                    </Grid>

                            </Grid>

                            <Box className='d-flex justify-content-end mt-4'>

                                <Button
                                    variant='contained'
                                    onClick={handleGuardar}
                                    disabled={guardando}
                                    startIcon={<FontAwesomeIcon icon={faSave} style={{ fontSize: 11 }} />}
                                >
                                    {Lang('GUARDAR_DATOS')}
                                </Button>

                            </Box>
                                </>
                        }

                        <ModalStatusServer
                            fetchData={fetchUpdateData}
                            activeLoading
                            // Al cerrar se limpia la respuesta del guardado: si se deja,
                            // el diálogo se vuelve a abrir solo al volver a esta
                            // subpestaña, porque `fetchUpdateData` sigue con el 200.
                            onClose={resetUpdate}
                            messageStatus={
                                {
                                    200:
                                        <>
                                            <FontAwesomeIcon icon={faCheckCircle} className='color-green fa-2x' />

                                            <Typography sx={{ mt: 2 }}>
                                                {fetchUpdateData.data?.message || Lang('DATOS_EMPRESA_ACTUALIZADOS')}
                                            </Typography>
                                        </>,
                                    403:
                                        <>
                                            <FontAwesomeIcon icon={faTimesCircle} className='color-red fa-2x' />

                                            <Typography sx={{ mt: 2 }}>
                                                {fetchUpdateData.error?.error || Lang('SIN_ACCESO_EMPRESA')}
                                            </Typography>
                                        </>,
                                    422:
                                        <>
                                            <FontAwesomeIcon icon={faTimesCircle} className='color-red fa-2x' />

                                            <Typography sx={{ mt: 2 }}>
                                                {Object.values(fetchUpdateData?.error?.errors || {}).flat()[0] || Lang('ERROR_GUARDAR_DATOS')}
                                            </Typography>
                                        </>,
                                }
                            }
                        />

                    </Box>
            }

        </Paper>
    )
}
